import { env } from '@/shared/lib/env';

import type { PushState } from './types';

/**
 * PRAT-165 — web push do app do cliente pelo OneSignal (SDK v16, carregado só
 * quando o cliente aceita receber). Sem `VITE_ONESIGNAL_APP_ID` o app não
 * oferece push; com `VITE_PUSH_SIMULATOR=1` (só local) a inscrição é simulada.
 */
const SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
const SIM_KEY = 'pratigo:push-sim-id';

const appId = () => env.VITE_ONESIGNAL_APP_ID;
export const isSimulator = () => !appId() && env.VITE_PUSH_SIMULATOR === '1';

/**
 * PRAT-43: o OneSignal só inscreve no endereço cadastrado no app dele (Site URL).
 * Em outro endereço o SDK recusa — foi o que zerou os inscritos (o painel estava
 * em www.pratigo.app e o app roda em pratigo.app). Sem a lista, vale qualquer um.
 */
export function onPushHost(
  host = typeof window !== 'undefined' ? window.location.host : ''
): boolean {
  const list = (env.VITE_ONESIGNAL_HOSTS ?? '')
    .split(',')
    .map(h => h.trim().toLowerCase())
    .filter(Boolean);
  if (!list.length) return true;
  return list.includes(host.toLowerCase());
}

export const pushEnabled = () => (!!appId() && onPushHost()) || isSimulator();

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1)
  );
}

/**
 * PRAT-90: navegador embutido de app no iPhone (Instagram, Facebook, Google…) não
 * tem "Adicionar à Tela de Início": lá não dá para ensinar nem para inscrever.
 */
export function isIosInAppBrowser(
  ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
): boolean {
  return /FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|Line\/|GSA\/|LinkedInApp/i.test(ua);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true
  );
}

export function platform(): string {
  if (isIos()) return 'ios';
  if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)) return 'android';
  return 'desktop';
}

function browserSupportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export function pushState(): PushState {
  if (!pushEnabled()) return 'unsupported';
  if (isIos() && !isStandalone()) return isIosInAppBrowser() ? 'unsupported' : 'needs-home-screen';
  if (!browserSupportsPush()) return isSimulator() ? 'default' : 'unsupported';
  const p = Notification.permission;
  return p === 'granted' ? 'granted' : p === 'denied' ? 'denied' : 'default';
}

type OneSignalApi = {
  init: (opts: Record<string, unknown>) => Promise<void>;
  login: (externalId: string) => Promise<void>;
  Notifications: { requestPermission: () => Promise<void>; permission: boolean };
  User: {
    addTags: (tags: Record<string, string>) => void;
    PushSubscription: {
      id?: string | null;
      optedIn?: boolean;
      optIn: () => Promise<void>;
      addEventListener: (ev: 'change', cb: () => void) => void;
    };
  };
};

let sdk: Promise<OneSignalApi> | null = null;

function describeError(e: unknown): string {
  if (e instanceof Error) return e.name === 'Error' ? e.message : `${e.name}: ${e.message}`;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e) ?? String(e);
  } catch {
    return String(e);
  }
}

const stageError = (stage: string, e: unknown) => new Error(`${stage}: ${describeError(e)}`);

/** PRAT-90 — o motivo gravado em `push_prompt_events`: etapa, erro e a permissão na hora. */
export function pushErrorReason(e: unknown): string {
  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'sem-api';
  return `${describeError(e)} [permissao=${permission}]`.slice(0, 200);
}

/** PRAT-90: navegador que o SDK recusa nunca responde; sem isto o botão fica em "Ativando...". */
function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    p.then(
      v => {
        clearTimeout(t);
        resolve(v);
      },
      e => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

function loadOneSignal(): Promise<OneSignalApi> {
  if (sdk) return sdk;
  sdk = new Promise<OneSignalApi>((resolve, reject) => {
    const w = window as unknown as { OneSignalDeferred?: Array<(os: OneSignalApi) => void> };
    w.OneSignalDeferred = w.OneSignalDeferred || [];
    w.OneSignalDeferred.push(async os => {
      try {
        // PRAT-201: app do OneSignal em "Typical Site" ignora o service worker do
        // código sem o override; aqui ele fica na raiz, como o painel espera.
        await os.init({
          appId: appId(),
          serviceWorkerPath: 'OneSignalSDKWorker.js',
          serviceWorkerOverrideForTypical: true,
        });
        resolve(os);
      } catch (e) {
        // PRAT-90: o SDK só inicia uma vez por página. Tentar de novo só trocaria o
        // motivo real por "SDK already initialized"; este erro fica até recarregar.
        reject(stageError('init', e));
      }
    });
    if (!document.querySelector(`script[src="${SDK_URL}"]`)) {
      const s = document.createElement('script');
      s.src = SDK_URL;
      s.defer = true;
      s.onerror = () => {
        // Aqui dá para tentar de novo: tira o script e a espera que ficaria na fila.
        s.remove();
        w.OneSignalDeferred = [];
        sdk = null;
        reject(new Error('sdk: o script do OneSignal não carregou'));
      };
      document.head.appendChild(s);
    }
  });
  return sdk;
}

function waitForSubscriptionId(os: OneSignalApi, timeoutMs = 10000): Promise<string | null> {
  const now = os.User.PushSubscription.id;
  if (now) return Promise.resolve(now);
  return new Promise(resolve => {
    const t = setTimeout(() => resolve(os.User.PushSubscription.id ?? null), timeoutMs);
    os.User.PushSubscription.addEventListener('change', () => {
      const id = os.User.PushSubscription.id;
      if (id) {
        clearTimeout(t);
        resolve(id);
      }
    });
  });
}

/** Id do aparelho já inscrito (sem pedir permissão), ou null. */
export async function currentSubscriptionId(): Promise<string | null> {
  if (isSimulator()) {
    try {
      return localStorage.getItem(SIM_KEY);
    } catch {
      return null;
    }
  }
  if (!appId() || pushState() !== 'granted') return null;
  try {
    const os = await loadOneSignal();
    return os.User.PushSubscription.id ?? null;
  } catch {
    return null;
  }
}

/** Liga a inscrição ao cliente (external_id) e marca a loja (tags), sem pedir nada. */
async function identify(os: OneSignalApi, userId?: string | null, merchantId?: string | null) {
  if (userId) await os.login(userId).catch(() => undefined);
  if (merchantId) {
    try {
      os.User.addTags({ store_id: merchantId, [`loja_${merchantId}`]: '1' });
    } catch {
      /* tag é extra: o envio da API usa a inscrição gravada por loja */
    }
  }
}

/**
 * PRAT-43 — aparelho que já deu permissão: refaz o vínculo com o cliente e a loja
 * (login depois da inscrição, ou cliente que voltou por outra loja).
 */
export async function syncPushIdentity(
  userId?: string | null,
  merchantId?: string | null
): Promise<void> {
  if (isSimulator() || !appId() || pushState() !== 'granted') return;
  try {
    await identify(await loadOneSignal(), userId, merchantId);
  } catch {
    /* sem SDK: a próxima visita tenta de novo */
  }
}

/**
 * Pede a permissão (precisa vir de um toque do cliente — no iPhone é regra) e
 * devolve o id da inscrição, ou null quando o cliente recusou. Lança quando o
 * navegador/OneSignal falha (conta como `error` na taxa de aceite).
 */
export async function enablePush(
  userId?: string | null,
  merchantId?: string | null
): Promise<string | null> {
  if (isSimulator()) {
    if (browserSupportsPush() && Notification.permission === 'default') {
      const answer = await Notification.requestPermission();
      if (answer === 'denied') return null;
    }
    let id: string | null = null;
    try {
      id = localStorage.getItem(SIM_KEY);
      if (!id) {
        id = `sim-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(SIM_KEY, id);
      }
    } catch {
      id = `sim-${Math.random().toString(36).slice(2, 10)}`;
    }
    return id;
  }

  const os = await withTimeout(loadOneSignal(), 30000, 'init: sem resposta em 30 s');
  try {
    await os.Notifications.requestPermission();
  } catch (e) {
    throw stageError('permissao', e);
  }
  if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return null;
  try {
    if (!os.User.PushSubscription.optedIn) await os.User.PushSubscription.optIn();
    await identify(os, userId, merchantId);
  } catch (e) {
    throw stageError('inscricao', e);
  }
  const id = await waitForSubscriptionId(os);
  if (!id) throw new Error('inscricao: o OneSignal não devolveu a inscrição');
  return id;
}
